/**
 * OCT20 WS-ROLL (FD-04) — the "Delete Entire Rollover" warning.
 *
 * Opened from a ticket's Delete when the server answers that the ticket is one day of a multi-day rollover
 * (ROLLOVER_CHAIN_MEMBER — a single day is never deleted alone). It lists every affected day (from the server's own
 * preview), requires a reason, and offers exactly two choices: "Delete Entire Rollover" or "Cancel". Cancel writes
 * nothing. The server re-derives the days under its own locks and decides every refusal; this dialog only reports.
 */
import { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ParkingSpotPickerDialog } from '@/components/birdseye/ParkingSpotPickerDialog';
import { toTerminalLocation, type TerminalLocationChoice } from '@/lib/tickets/deleteLocationPrompt';
import {
  deleteRolloverChain, describeChainDeleteRefusal, isChainDeleteSettled, previewRolloverChainDelete,
  type ChainBlocker, type ChainDeletePreview, type ChainDeleteResult,
} from '@/utils/deleteRolloverChain';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ticketId: string;
  shopId: string;
  /** The reason already chosen in the ordinary delete dialog, offered as a starting point (still editable). */
  initialReason?: string | null;
  onDeleted: (result: ChainDeleteResult, reason: string) => void;
}

export const MIN_CHAIN_DELETE_REASON = 3;

export function RolloverChainDeleteDialog({ open, onOpenChange, ticketId, shopId, initialReason, onDeleted }: Props) {
  const [preview, setPreview] = useState<ChainDeletePreview | null>(null);
  const [reason, setReason] = useState(initialReason ?? '');
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<ChainBlocker | null>(null);
  const [askLocation, setAskLocation] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setPreview(null);
    setRefusal(null);
    setReason(initialReason ?? '');
    void previewRolloverChainDelete(ticketId).then((p) => { if (!cancelled) setPreview(p); });
    return () => { cancelled = true; };
  }, [open, ticketId, initialReason]);

  const notAuthorized = preview?.outcome === 'CHAIN_DELETE_NOT_AUTHORIZED';
  const blocker = refusal ?? (preview?.blocker && preview.blocker.outcome !== 'LOCATION_REQUIRED' ? preview.blocker : null);
  const reasonOk = reason.trim().length >= MIN_CHAIN_DELETE_REASON;
  const canSubmit = !!preview && preview.outcome === 'PREVIEW' && preview.mayDelete && reasonOk && !busy && !blocker;

  const submit = async (location: TerminalLocationChoice | null) => {
    setBusy(true);
    try {
      const r = await deleteRolloverChain(ticketId, reason.trim(), location?.kind ?? null, location?.spotId ?? null);
      if (isChainDeleteSettled(r.outcome)) {
        onDeleted(r, reason.trim());
        onOpenChange(false);
        return;
      }
      if (r.outcome === 'LOCATION_REQUIRED') { setAskLocation(true); return; }
      setRefusal(r.blocker ?? { outcome: r.outcome, blockedTicketId: null, note: r.note });
    } finally {
      setBusy(false);
    }
  };

  const segments = preview?.segments ?? [];
  return (
    <>
      <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o); }}>
        <DialogContent className="max-w-lg" data-testid="rollover-chain-delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete entire rollover?</DialogTitle>
            <DialogDescription>
              This ticket is one day of a multi-day repair. A single day cannot be deleted on its own. Deleting removes
              every day below from the boards and releases their scheduled time. Recorded labor, notes, diagnostic
              evidence and history are kept. A deleted rollover cannot be restored from the app.
            </DialogDescription>
          </DialogHeader>

          {!preview && <p className="text-sm text-muted-foreground">Loading the days of this repair…</p>}
          {notAuthorized && (
            <p className="text-sm text-destructive" data-testid="rollover-chain-delete-not-authorized">
              {describeChainDeleteRefusal(preview!.blocker)}
            </p>
          )}
          {preview && preview.outcome !== 'PREVIEW' && !notAuthorized && (
            <p className="text-sm text-destructive">{describeChainDeleteRefusal(preview.blocker)}</p>
          )}

          {segments.length > 0 && (
            <div>
              <p className="mb-1 text-sm font-medium">Affected days ({segments.length})</p>
              <ul className="max-h-48 space-y-1 overflow-y-auto rounded border p-2 text-sm" data-testid="rollover-chain-delete-segments">
                {segments.map((s) => (
                  <li key={s.ticketId} className="flex flex-wrap items-center justify-between gap-2" data-testid="rollover-chain-delete-segment">
                    <span>
                      {s.ticketNumber != null ? `#${s.ticketNumber}` : s.ticketId}
                      {s.scheduledDate ? ` · ${s.scheduledDate}` : ''}
                      {s.isHead ? ' · repair jobs' : ''}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {s.status}{s.recordedMinutes > 0 ? ` · ${s.recordedMinutes} min recorded (kept)` : ''}{s.inBay ? ' · in a bay' : ''}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {blocker && (
            <p className="text-sm text-destructive" role="alert" data-testid="rollover-chain-delete-blocker">
              Nothing was deleted. {describeChainDeleteRefusal(blocker, segments)}
            </p>
          )}

          {preview?.outcome === 'PREVIEW' && (
            <div className="space-y-1">
              <label htmlFor="rollover-chain-delete-reason" className="text-sm font-medium">Reason (required)</label>
              <Textarea id="rollover-chain-delete-reason" rows={2} maxLength={500} value={reason}
                onChange={(e) => { setReason(e.target.value); setRefusal(null); }}
                placeholder="Why is the whole repair being deleted?" data-testid="rollover-chain-delete-reason" />
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid="rollover-chain-delete-cancel">
              Cancel
            </Button>
            {!notAuthorized && (
              <Button variant="destructive" disabled={!canSubmit} onClick={() => { void submit(null); }}
                data-testid="rollover-chain-delete-confirm">
                Delete Entire Rollover
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ParkingSpotPickerDialog
        isOpen={askLocation}
        shopId={shopId}
        ticketId={ticketId}
        selectionOnly
        requireExplicitSelection
        onClose={() => setAskLocation(false)}
        onSelect={(spotId) => { setAskLocation(false); void submit(toTerminalLocation(spotId)); }}
      />
    </>
  );
}
